<div class="electronic-invoice-to-generate">
  <div class="alert alert-info banner" role="alert">
    <p class="mb-2">{{ 'to_generate.electronic.banner_intro' | translate }}</p>
    <ol class="mb-0">
      <li>{{ 'to_generate.electronic.banner_address' | translate }}</li>
      <li>{{ 'to_generate.electronic.banner_activity_id' | translate }}</li>
      <li>{{ 'to_generate.electronic.banner_amounts' | translate }}</li>
    </ol>
  </div>

  <div *ngIf="loading" class="text-center py-4">
    <app-spinner></app-spinner>
  </div>

  <div class="po-selector mb-3" *ngIf="!loading">
    <label class="form-label" for="electronic-purchase-order">
      {{ 'to_generate.electronic.purchase_order_label' | translate }}
    </label>
    <ng-select
      inputId="electronic-purchase-order"
      [items]="purchaseOrderOptions"
      bindLabel="label"
      bindValue="value"
      [searchable]="true"
      [clearable]="false"
      [placeholder]="'to_generate.electronic.purchase_order_placeholder' | translate"
      [ngModel]="selectedPurchaseOrderValue"
      (ngModelChange)="onPurchaseOrderChange($event)"
    >
      <ng-template ng-notfound-tmp>
        <div class="ng-option ng-option-disabled">
          {{ 'global.dropdown.not-found' | translate }}
        </div>
      </ng-template>
    </ng-select>
  </div>

  <ng-container *ngIf="!loading && hasSelectedPurchaseOrder">
    <div class="row slide-in-animation">
      <div class="col" [ngClass]="{'col-md-6': !advancedOpen, 'col-md-8': advancedOpen}">
        <div class="text-right" [ngClass]="{'offset-advanced-search': advancedOpen}">
          <button class="advanced-search-button" (click)="toggleAdvanced()">
            <u>{{ 'to_generate.electronic.advanced_search' | translate }}</u>
            <fa-icon class="ml-1" [icon]="advancedOpen ? faAngleDown : faAngleLeft"></fa-icon>
          </button>
        </div>

        <form
          [formGroup]="advancedForm"
          (ngSubmit)="searchAdvanced()"
          *ngIf="advancedOpen; else basicSearchBlock"
          @fadeIn
        >
          <div class="row">
            <div class="col form-group">
              <label class="mb-1" for="electronic-activity-id-input">{{ 'to_generate.electronic.columns.activity_id' | translate }}</label>
              <input type="text" class="form-control" id="electronic-activity-id-input" formControlName="activityId"/>
            </div>
            <div class="col form-group">
              <label class="mb-1" for="electronic-client-input">{{ 'to_generate.electronic.columns.client' | translate }}</label>
              <input type="text" class="form-control" id="electronic-client-input" formControlName="clientName"/>
            </div>
            <div class="col form-group" style="flex-grow: 1.5">
              <label class="mb-1" for="electronic-period-input">{{ 'to_generate.electronic.columns.period' | translate }}</label>
              <div class="d-flex">
                <input
                  type="text"
                  class="form-control"
                  id="electronic-period-input"
                  formControlName="period"
                  bsDatepicker
                  readonly
                  [bsConfig]="{ dateInputFormat: 'MM/YYYY', minMode: 'month', containerClass: 'theme-red' }"
                />
                <span
                  *ngIf="advancedForm.get('period').value"
                  class="adv-search-date-icon"
                  (click)="clearAdvancedSearchPeriod()"
                >×</span>
              </div>
            </div>
            <div class="col form-group" style="flex-grow: 1.5">
              <label class="mb-1" for="electronic-type-input">{{ 'to_generate.electronic.columns.type' | translate }}</label>
              <ng-select
                formControlName="types"
                [items]="typeOptions"
                [multiple]="true"
                [closeOnSelect]="false"
                [hideSelected]="false"
                [searchable]="false"
                [labelForId]="'electronic-type-input'"
                class="ng-select-custom"
                id="electronic-type-input"
              >
                <ng-template ng-option-tmp let-item="item" let-item$="item$" let-index="index">
                  <input id="electronic-type-item-{{ index }}" type="checkbox" [checked]="item$.selected"/>{{ typeLabel(item) }}
                </ng-template>
                <ng-template ng-multi-label-tmp let-items="items" let-clear="clear">
                  <div class="ng-value" *ngFor="let item of items | slice: 0:1">
                    <span class="ng-value-label">{{ typeLabel(item) }}</span>
                  </div>
                  <div class="ng-value" *ngIf="items.length > 1">
                    <span class="ng-value-label">{{ items.length - 1 }} {{ 'global.common.more-ellipsis' | translate }}</span>
                  </div>
                </ng-template>
              </ng-select>
            </div>
            <div class="col d-flex align-items-end pl-0">
              <button class="btn btn-outline-primary px-5 mb-3" type="submit" [disabled]="advancedForm.pristine">
                {{ 'to_generate.electronic.search' | translate }}
              </button>
            </div>
          </div>
        </form>

        <ng-template #basicSearchBlock>
          <div class="form-group basic-search-group" @fadeIn>
            <input
              type="text"
              class="form-control"
              id="electronic-quick-search"
              [placeholder]="'to_generate.electronic.search_placeholder' | translate"
              [value]="quickSearch"
              (input)="onQuickSearchChange($event.target.value)"
            />
          </div>
        </ng-template>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-material box-shadow-hover">
        <thead class="thead-normal">
        <tr>
          <th>{{ 'to_generate.electronic.columns.activity_id' | translate }}</th>
          <th>{{ 'to_generate.electronic.columns.client' | translate }}</th>
          <th>{{ 'to_generate.electronic.columns.period' | translate }}</th>
          <th class="text-end">{{ 'to_generate.electronic.columns.unit_price' | translate }}</th>
          <th class="text-end">{{ 'to_generate.electronic.columns.quantity' | translate }}</th>
          <th class="text-end">{{ 'to_generate.electronic.columns.amount_ht' | translate }}</th>
          <th>{{ 'to_generate.electronic.columns.type' | translate }}</th>
          <th>{{ 'to_generate.electronic.columns.payment_delay' | translate }}</th>
        </tr>
        </thead>
        <tbody>
        <tr *ngFor="let line of displayedLines">
          <td>
            <span class="copyable" (click)="copyValue(line.activityId)"
                  [tooltip]="'to_generate.electronic.copy' | translate" container="body">
              {{ line.activityId }}
              <fa-icon class="copy-icon" [icon]="faCopy"></fa-icon>
            </span>
          </td>
          <td>
            <span class="copyable" (click)="copyValue(line.clientName)"
                  [tooltip]="'to_generate.electronic.copy' | translate" container="body">
              {{ line.clientName }}
              <fa-icon class="copy-icon" [icon]="faCopy"></fa-icon>
            </span>
          </td>
          <td>{{ line.periodLabel }}</td>
          <td class="text-end">{{ line.unitPrice | localizedCurrency: (line.currency || 'EUR') }}</td>
          <td class="text-end">{{ line.quantity }}</td>
          <td class="text-end">{{ line.amountExcludingTax | localizedCurrency: (line.currency || 'EUR') }}</td>
          <td>{{ typeLabel(line.type) }}</td>
          <td>{{ paymentDelayLabel(line.paymentDelayDays) }}</td>
        </tr>
        <tr *ngIf="displayedLines.length === 0">
          <td colspan="8" class="text-center py-3">
            <app-no-data></app-no-data>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </ng-container>
</div>
